import { useState, useEffect, useRef } from 'react';

interface NotesEditorProps {
  value: string;
  onSave: (notes: string) => void;
  disabled?: boolean;
}

export function NotesEditor({ value, onSave, disabled }: NotesEditorProps) {
  const [text, setText] = useState(value);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    setText(value);
  }, [value]);

  const handleChange = (newText: string) => {
    setText(newText);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = window.setTimeout(async () => {
      if (newText !== value) {
        setSaveStatus('saving');
        try {
          await onSave(newText);
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 3000);
        } catch {
          setSaveStatus('error');
        }
      }
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="notes-editor">
      <div className="notes-header">
        <label className="input-label">Notes</label>
        {saveStatus === 'saving' && <span className="saving-indicator">Saving...</span>}
        {saveStatus === 'saved' && (
          <span className="saving-indicator" style={{ color: 'var(--success)' }}>
            Saved
          </span>
        )}
        {saveStatus === 'error' && (
          <span className="saving-indicator" style={{ color: 'var(--error)' }}>
            Failed to save
          </span>
        )}
      </div>
      <textarea
        className="notes-textarea"
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="Add notes about this property..."
        disabled={disabled}
        rows={4}
      />
    </div>
  );
}
